import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface ToastProps {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(id), 200);
  }, [id, onClose]);

  useEffect(() => {
    if (duration <= 0) return;
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
    }, 50);
    const timer = setTimeout(() => {
      clearInterval(interval);
      handleClose();
    }, duration);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [duration, handleClose]);

  const typeConfig = {
    success: {
      icon: CheckCircle,
      accent: 'bg-success',
      track: 'bg-success-background',
      iconBg: 'bg-success-background text-success',
    },
    error: {
      icon: XCircle,
      accent: 'bg-destructive',
      track: 'bg-destructive-background',
      iconBg: 'bg-destructive-background text-destructive',
    },
    warning: {
      icon: AlertCircle,
      accent: 'bg-warning',
      track: 'bg-warning-background',
      iconBg: 'bg-warning-background text-warning',
    },
    info: {
      icon: Info,
      accent: 'bg-info',
      track: 'bg-info-background',
      iconBg: 'bg-info-background text-info',
    },
  };

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'toast-enter flex w-full min-w-[260px] max-w-[340px] flex-col overflow-hidden rounded-lg border border-border bg-card text-card-foreground shadow-md',
        isExiting && 'toast-exit opacity-0 translate-x-4'
      )}
    >
      <div className="flex flex-1 items-start gap-2 px-3 pt-2.5 pb-2">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
            config.iconBg
          )}
        >
          <Icon className="h-4 w-4" aria-hidden strokeWidth={2.25} />
        </span>
        <div className="min-w-0 flex-1 overflow-hidden pt-0.5">
          <p className="text-sm font-semibold leading-tight text-foreground truncate">
            {title}
          </p>
          {message && (
            <p className="mt-0.5 line-clamp-2 text-xs font-normal text-muted-foreground break-words">
              {message}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-card"
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
      {duration > 0 && (
        <div
          className={cn('h-0.5 w-full overflow-hidden', config.track)}
          aria-hidden
        >
          <div
            className={cn(
              'h-full rounded-r-full transition-[width] duration-75 ease-linear',
              config.accent
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
